import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  House, Users, Student, HandCoins, Receipt, Gear, ChartBar,
  UserMinus, SignOut, List, CaretLeft, UploadSimple
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: House, exact: true },
  { path: '/donors', label: 'Donors Hub', icon: HandCoins },
  { path: '/recipients', label: 'Recipients Hub', icon: Student },
  { path: '/dropouts', label: 'Dropout Hub', icon: UserMinus },
  { path: '/expenses', label: 'Expenses Hub', icon: Receipt },
  { path: '/import', label: 'Import Hub', icon: UploadSimple },
  { path: '/admin', label: 'Admin Hub', icon: Gear },
  { path: '/reports', label: 'Reports Hub', icon: ChartBar },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const currentPage = NAV_ITEMS.find(
    item => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
  );

  return (
    <div className="flex h-screen bg-[#F8F9FA] overflow-hidden" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`donex-sidebar flex flex-col bg-[#1B1B1F] text-white transition-all duration-200 ${collapsed ? 'collapsed' : ''}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-white/10 shrink-0">
          <div className="w-8 h-8 bg-[#0F6CBD] rounded-sm flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm font-['Chivo']">D</span>
          </div>
          {!collapsed && (
            <span className="sidebar-label text-lg font-semibold font-['Chivo'] tracking-tight">DonEx</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? 'bg-[#0F6CBD] text-white'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} className="shrink-0" />
                {!collapsed && <span className="sidebar-label">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center h-10 border-t border-white/10 text-slate-400 hover:text-white transition-colors"
          data-testid="sidebar-collapse-btn"
        >
          <CaretLeft size={18} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Top Bar */}
        <header className="flex items-center justify-between h-14 px-6 bg-white border-b border-slate-200 shrink-0" data-testid="top-header">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="lg:hidden text-slate-500 hover:text-slate-700"
            >
              <List size={20} />
            </button>
            <h1 className="text-lg font-semibold text-slate-900 font-['Chivo']" data-testid="page-title">
              {currentPage?.label || 'DonEx'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-sm" data-testid="user-menu-btn">
                  <div className="w-7 h-7 bg-[#0F6CBD] rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:inline text-slate-700">{user?.name || 'User'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-xs text-slate-500">{user?.email}</DropdownMenuItem>
                <DropdownMenuItem className="text-xs text-slate-500 capitalize">{user?.role}</DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="logout-btn"
                  className="text-red-600 cursor-pointer"
                >
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="donex-content p-6 lg:p-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
